import { GreaterAreaDropdown, StateField } from '@/components/dropdowns';
import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import type { Neighborhood } from '@/config/objects/neighborhoods';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  FormPageLayout,
  Input,
  RichTextEditor,
  Skeleton,
  SwitchField,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NeighborhoodFormData {
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  description: string;
  featuredImageId: string | undefined;
  greaterAreaId: string | undefined;
  country: string;
  state: string;
  city: string;
}

const DEFAULT_FORM: NeighborhoodFormData = {
  name: '',
  status: 'published',
  description: '',
  featuredImageId: undefined,
  greaterAreaId: undefined,
  country: 'US',
  state: '',
  city: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NeighborhoodFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  // Form state
  const [formData, setFormData] = useState<NeighborhoodFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  const updateField = useCallback(
    <K extends keyof NeighborhoodFormData>(key: K, value: NeighborhoodFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // -----------------------------------------------------------------------
  // Load existing neighborhood (edit mode)
  // -----------------------------------------------------------------------
  const {
    data: neighborhoodResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: Neighborhood }>({
    queryKey: ['Neighborhood', itemId],
    queryFn: () => apiClient.get(`/neighborhoods/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!neighborhoodResponse?.data) return;
    const n = neighborhoodResponse.data;
    setFormData({
      name: n.name ?? '',
      status: n.status ?? 'published',
      description: n.description ?? '',
      featuredImageId: n.featuredImageId ?? undefined,
      greaterAreaId: n.greaterAreaId ?? undefined,
      country: n.country ?? 'US',
      state: n.state ?? '',
      city: n.city ?? '',
    });
    setLastSaved(new Date(n.updatedAt));
    setFormReady(true);
  }, [neighborhoodResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Neighborhood not found');
      navigate('/neighborhoods');
    }
  }, [isError, navigate]);

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.greaterAreaId) errs.greaterAreaId = 'Greater area is required';
    if (!formData.country.trim()) errs.country = 'Country is required';
    if (!formData.state.trim()) errs.state = 'State is required';
    if (!formData.city.trim()) errs.city = 'City is required';

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`field-${firstKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  // -----------------------------------------------------------------------
  // Prepare payload
  // -----------------------------------------------------------------------
  const preparePayload = () => ({
    name: formData.name.trim(),
    status: formData.status,
    description: formData.description || null,
    featuredImageId: formData.featuredImageId || null,
    greaterAreaId: formData.greaterAreaId || null,
    country: formData.country.trim(),
    state: formData.state.trim(),
    city: formData.city.trim(),
  });

  // -----------------------------------------------------------------------
  // Save mutation
  // -----------------------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: Neighborhood }>(`/neighborhoods/${itemId}`, payload);
      }
      return apiClient.post<{ data: Neighborhood }>('/neighborhoods', payload);
    },
    onSuccess: (response: { data: Neighborhood }) => {
      queryClient.invalidateQueries({ queryKey: ['Neighborhood'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Neighborhood saved successfully');
        navigate(-1);
      } else {
        toast.success('Neighborhood created successfully');
        navigate(`/neighborhoods/${response.data.id}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save neighborhood');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;
    saveMutation.mutate(preparePayload());
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/neighborhoods/${itemId}`);
    } else {
      navigate('/neighborhoods');
    }
  };

  // -----------------------------------------------------------------------
  // Loading skeleton
  // -----------------------------------------------------------------------
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">
              {isEdit ? 'Edit Neighborhood' : 'Create Neighborhood'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit
                ? formData.name || 'Update neighborhood details'
                : 'Add a new neighborhood to the platform'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div ref={firstErrorRef} className="space-y-6">
          {/* General Information */}
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 border-b">
              <CardTitle className="text-zinc-900">General Information</CardTitle>
              <CardDescription>Core neighborhood details</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter neighborhood name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <ImageUploadDropzone targetAspectRatio={4/3}
                value={formData.featuredImageId}
                onChange={(value) => updateField('featuredImageId', value)}
              />

              <SwitchField
                label="Published"
                description="Make this neighborhood visible to users"
                checked={formData.status === 'published'}
                onCheckedChange={(checked) =>
                  updateField('status', checked ? 'published' : 'draft')
                }
              />

              <FormFieldGroup name="description" label="Description">
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => updateField('description', value ?? '')}
                />
              </FormFieldGroup>
            </CardContent>
          </Card>

          {/* Location */}
          <Card className="rounded-xl border bg-card shadow-sm">
            <CardHeader className="px-6 py-4 border-b">
              <CardTitle className="text-zinc-900">Location</CardTitle>
              <CardDescription>Where is the neighborhood located?</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup
                name="greaterAreaId"
                label="Greater Area"
                required
                error={errors.greaterAreaId}
              >
                <GreaterAreaDropdown
                  value={formData.greaterAreaId ?? ''}
                  onChange={(value) => updateField('greaterAreaId', value || undefined)}
                  placeholder="Select Greater Area"
                  className={cn(errors.greaterAreaId && 'border-destructive')}
                />
              </FormFieldGroup>

              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup name="country" label="Country" required error={errors.country}>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => updateField('country', e.target.value)}
                    placeholder="Country"
                    className={cn(errors.country && 'border-destructive')}
                  />
                </FormFieldGroup>

                <FormFieldGroup name="state" label="State" required error={errors.state}>
                  <StateField
                    value={formData.state}
                    onChange={(value) => updateField('state', value)}
                    country={formData.country}
                    placeholder="Select State"
                    className={cn(errors.state && 'border-destructive')}
                  />
                </FormFieldGroup>
              </div>

              <FormFieldGroup name="city" label="City" required error={errors.city}>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="City"
                  className={cn(errors.city && 'border-destructive')}
                />
              </FormFieldGroup>
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            loading={saveMutation.isPending}
            icon={Save}
          >
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
